import { sourTwMerge } from '@plaidev/sour-react';
import { NAVI_FIXED_THEME_VARS } from '@rt/global-navi/react';
import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

export const Wrapper: React.FC<PropsWithChildren> = ({ children }) => {
  return <div className="isolate flex h-full">{children}</div>;
};

export const Main: React.FC<PropsWithChildren> = ({ children }) => {
  return <div className="isolate grow basis-0 min-w-0 h-full relative">{children}</div>;
};

export namespace Sidebar {
  export type Props = {
    width: { expanded: string; collapsed: string };
    expanded: boolean;
    pinned: boolean;
    onEnter: () => void;
    onLeave: () => void;
    className?: string;
    // 右端ドラッグでの幅変更を有効化したいときに指定する。
    // 親が px 単位の状態を保持し、ドラッグ中は連続的に呼び出される。
    onResize?: (widthPx: number) => void;
    resizeRange?: { min: number; max: number };
  };
}
export const Sidebar: React.FC<PropsWithChildren<Sidebar.Props>> = ({
  width,
  expanded,
  pinned,
  onEnter,
  onLeave,
  className,
  onResize,
  resizeRange,
  children,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onResize || !sidebarRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      dragStateRef.current = {
        startX: e.clientX,
        startWidth: sidebarRef.current.getBoundingClientRect().width,
      };
      setIsDragging(true);
    },
    [onResize],
  );

  useEffect(() => {
    if (!isDragging) return;
    const min = resizeRange?.min ?? 0;
    const max = resizeRange?.max ?? Number.POSITIVE_INFINITY;
    const handleMove = (e: MouseEvent) => {
      const state = dragStateRef.current;
      if (!state || !onResize) return;
      const next = Math.min(max, Math.max(min, state.startWidth + (e.clientX - state.startX)));
      onResize(next);
    };
    const handleUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    // ドラッグ中はテキスト選択や他要素のホバー反応を抑止する
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isDragging, onResize, resizeRange?.min, resizeRange?.max]);

  const transitionClass = isDragging ? '' : 'transition-all duration-200';
  const showResizeHandle = !!onResize && expanded;

  return (
    <>
      <div className={transitionClass} style={{ width: pinned ? width.expanded : width.collapsed }} />
      <div
        ref={sidebarRef}
        className={sourTwMerge('shrink-0 flex flex-col absolute z-10 h-screen overflow-hidden', transitionClass, className)}
        style={{ width: expanded ? width.expanded : width.collapsed, ...NAVI_FIXED_THEME_VARS }}
        onMouseEnter={onEnter}
        onFocus={onEnter}
        onMouseLeave={onLeave}
        onBlur={onLeave}
      >
        {children}
        {showResizeHandle && (
          <div
            aria-hidden="true"
            onMouseDown={handleResizeMouseDown}
            className={sourTwMerge(
              'absolute top-0 right-0 z-20 h-full w-1 cursor-col-resize select-none',
              'hover:bg-muted/60',
              isDragging && 'bg-muted',
            )}
          />
        )}
      </div>
    </>
  );
};
